// src/components/EditDeploymentForm.js
import React, { useEffect, useState, useMemo } from "react";
import "./AddDeploymentForm.css";

export default function EditDeploymentForm({
  deploymentId,
  userId,
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);

  const [deploySelected, setDeploySelected] = useState([]);
  const [collectSelected, setCollectSelected] = useState([]);
  const [transferSelected, setTransferSelected] = useState([]);

  const [deployment, setDeployment] = useState(null);

  const [form, setForm] = useState({
    deploymenttype_id: "",
    vendor_id: "",
    deployment_location_id: "",
  });

  useEffect(() => {
    if (!deploymentId) return;

    setLoading(true);
    setError("");

    fetch(`/api/deployments/${deploymentId}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          throw new Error(data.error || "Failed to load deployment.");
        }
        return data;
      })
      .then(({ deployment, items }) => {
        setDeployment(deployment);
        setItems(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        console.error("View deployment error:", err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [deploymentId]);

  const [deploymentTypes, setDeploymentTypes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);

  // Full inventory + inventory at deployment location
  const [inventory, setInventory] = useState([]);
  const [locationInventory, setLocationInventory] = useState([]);

  // Devices currently in this deployment
  const [deployDevices, setDeployDevices] = useState([]);
  const [collectDevices, setCollectDevices] = useState([]);
  const [transferDevices, setTransferDevices] = useState([]);

  // Wizard step: 1=INFO, 2=DEPLOY, 3=COLLECT, 4=TRANSFER
  const [step, setStep] = useState(1);

  // Search per page
  const [deploySearch, setDeploySearch] = useState("");
  const [collectSearch, setCollectSearch] = useState("");
  const [transferSearch, setTransferSearch] = useState("");

  // Optional: include PO in COLLECT inventory list
  const [collectIncludePO, setCollectIncludePO] = useState(false);

  // Default destination applied to newly-added COLLECT/TRANSFER devices.
  // Each device's destination can still be overridden individually in
  // the selected-items table below.
  const [defaultCollectToLocationId, setDefaultCollectToLocationId] = useState("");
  const [defaultTransferToLocationId, setDefaultTransferToLocationId] = useState("");

  // Devices that the backend refused to remove because they're no longer
  // PENDING (already scanned / collected / received). Shown after save.
  const [blockedRemovals, setBlockedRemovals] = useState([]);

  // ---------- Load dropdowns + base inventory ----------
  useEffect(() => {
    fetch("/api/deployment-types")
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && setDeploymentTypes(data))
      .catch((e) => console.error("Error loading deployment types:", e));

    fetch("/api/deployments/vendors")
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && setVendors(data))
      .catch((e) => console.error("Error loading vendors:", e));

    fetch("/api/locations")
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && setLocations(data))
      .catch((e) => console.error("Error loading locations:", e));

    // Same as Add form: full inventory list (DEPLOY source).
    // all=true bypasses pagination. The endpoint always returns
    // { items, total, page, limit, totalPages } now, never a bare array,
    // so unwrap `items` rather than checking Array.isArray on the response.
    fetch("/api/products?all=true")
      .then((r) => r.json())
      .then((data) => {
        const rows = Array.isArray(data) ? data : (data.items || []);
        setInventory(rows);
      })
      .catch((err) => {
        console.error("Error fetching inventory", err);
        setError("Failed to load inventory list.");
      });
  }, []);

  // ---------- Load deployment header + items ----------
  useEffect(() => {
    if (!deploymentId) return;

    setLoading(true);
    setError("");

    fetch(`/api/deployments/${deploymentId}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          throw new Error(data.error || "Failed to load deployment details.");
        }
        return data;
      })
      .then(({ deployment, items }) => {
        setDeployment(deployment || null);

        // Pre-fill header form
        setForm({
          deploymenttype_id: deployment?.deploymenttype_id || "",
          vendor_id: deployment?.vendor_id || "",
          deployment_location_id: deployment?.deployment_location_id || "",
        });

        const list = Array.isArray(items) ? items : [];
        setDeployDevices(list.filter((it) => it.action_type === "DEPLOY"));
        // Preserve each existing item's real to_location_id so we don't
        // accidentally overwrite it with a default the moment the form loads.
        setCollectDevices(
          list
            .filter((it) => it.action_type === "COLLECT")
            .map((it) => ({ ...it, to_location_id: it.to_location_id ?? "" }))
        );
        setTransferDevices(
          list
            .filter((it) => it.action_type === "TRANSFER")
            .map((it) => ({ ...it, to_location_id: it.to_location_id ?? "" }))
        );
      })
      .catch((e) => {
        console.error("Edit deployment load error:", e);
        setError(e.message);
      })
      .finally(() => setLoading(false));
  }, [deploymentId]);

  // ---------- Load inventory for selected deployment location ----------
  useEffect(() => {
    const locId = form.deployment_location_id;
    if (!locId) {
      setLocationInventory([]);
      return;
    }

    fetch(`/api/products/by-location/${locId}`)
      .then((r) => r.json())
      .then((rows) => {
        setLocationInventory(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        console.error("Error fetching products by location", err);
        setError("Failed to load inventory for selected location.");
      });
  }, [form.deployment_location_id]);

  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ---------- Helper: search + selected IDs ----------
  const filterBySearch = (list, query) => {
    if (!query.trim()) return list;
    const q = query.toLowerCase();
    return list.filter((i) => {
      return (
        (i.asset1_name || i.asset1_id || "")
          .toString()
          .toLowerCase()
          .includes(q) ||
        (i.imei_number || "").toLowerCase().includes(q) ||
        (i.serial_number || "").toLowerCase().includes(q) ||
        (i.location_name || "").toLowerCase().includes(q)
      );
    });
  };

  const allSelectedIds = useMemo(() => {
    const ids = [
      ...deployDevices.map((d) => d.inventory_id),
      ...collectDevices.map((d) => d.inventory_id),
      ...transferDevices.map((d) => d.inventory_id),
    ];
    return new Set(ids.filter(Boolean));
  }, [deployDevices, collectDevices, transferDevices]);

  // ---------- Available inventory per step (EXCLUDING already selected) ----------

  const deployAvailable = useMemo(() => {
    const base = inventory.filter(
      (i) => !allSelectedIds.has(i.inventory_id)
    );
    return filterBySearch(base, deploySearch);
  }, [inventory, allSelectedIds, deploySearch]);

  const collectAvailable = useMemo(() => {
    let base = locationInventory.filter((i) =>
      collectIncludePO
        ? i.inventory_type === "FOC" || i.inventory_type === "PO"
        : i.inventory_type === "FOC"
    );
    base = base.filter((i) => !allSelectedIds.has(i.inventory_id));
    return filterBySearch(base, collectSearch);
  }, [locationInventory, collectIncludePO, allSelectedIds, collectSearch]);

  const transferAvailable = useMemo(() => {
    let base = locationInventory.filter((i) => i.inventory_type === "PO");
    base = base.filter((i) => !allSelectedIds.has(i.inventory_id));
    return filterBySearch(base, transferSearch);
  }, [locationInventory, allSelectedIds, transferSearch]);

  // ---------- Add / remove device helpers ----------

  const addFromInventory = (inv, actionType) => {
    if (!inv || !inv.inventory_id) return;

    const newRow = {
      action_type: actionType,
      inventory_id: inv.inventory_id,
      asset1_name: inv.asset1_name,
      asset2_name: inv.asset2_name,
      imei_number: inv.imei_number,
      serial_number: inv.serial_number,
      from_location_name: inv.location_name || inv.from_location_name || "",
      to_location_name: inv.to_location_name || "",
      inventory_type: inv.inventory_type || "",
      to_location_id:
        actionType === "COLLECT"
          ? defaultCollectToLocationId || ""
          : actionType === "TRANSFER"
          ? defaultTransferToLocationId || ""
          : null,
    };

    if (actionType === "DEPLOY") {
      if (deployDevices.some((d) => d.inventory_id === inv.inventory_id)) return;
      setDeployDevices((prev) => [...prev, newRow]);
    } else if (actionType === "COLLECT") {
      if (collectDevices.some((d) => d.inventory_id === inv.inventory_id)) return;
      setCollectDevices((prev) => [...prev, newRow]);
    } else if (actionType === "TRANSFER") {
      if (transferDevices.some((d) => d.inventory_id === inv.inventory_id)) return;
      setTransferDevices((prev) => [...prev, newRow]);
    }
  };

  const removeFrom = (actionType, inventory_id) => {
    if (!inventory_id) return;
    if (actionType === "DEPLOY") {
      setDeployDevices((prev) =>
        prev.filter((d) => d.inventory_id !== inventory_id)
      );
    } else if (actionType === "COLLECT") {
      setCollectDevices((prev) =>
        prev.filter((d) => d.inventory_id !== inventory_id)
      );
    } else if (actionType === "TRANSFER") {
      setTransferDevices((prev) =>
        prev.filter((d) => d.inventory_id !== inventory_id)
      );
    }
  };

  // Update a single device's destination location without touching the rest
  const updateItemLocation = (actionType, inventory_id, newLocationId) => {
    const updater = (prev) =>
      prev.map((d) =>
        d.inventory_id === inventory_id
          ? { ...d, to_location_id: newLocationId }
          : d
      );
    if (actionType === "COLLECT") setCollectDevices(updater);
    else if (actionType === "TRANSFER") setTransferDevices(updater);
  };

  // ---------- Save ----------

  const handleSave = (e) => {
    e.preventDefault();
    if (!userId) {
      alert("User not detected. Please log in again.");
      return;
    }

    if (!form.deploymenttype_id || !form.deployment_location_id) {
      alert("Please select deployment type and location.");
      setStep(1);
      return;
    }

    // Every COLLECT/TRANSFER device needs a destination before saving —
    // otherwise it'll be saved with a null to_location_id.
    const missingCollectDest = collectDevices.filter((d) => !d.to_location_id);
    const missingTransferDest = transferDevices.filter((d) => !d.to_location_id);
    if (missingCollectDest.length || missingTransferDest.length) {
      alert(
        "Please set a destination location for every COLLECT/TRANSFER device before saving."
      );
      if (missingCollectDest.length) setStep(3);
      else setStep(4);
      return;
    }

    const payload = {
      deploymenttype_id: form.deploymenttype_id,
      vendor_id: form.vendor_id || null,
      deployment_location_id: form.deployment_location_id,
      user_id: userId,
      deploy_inventory_ids: deployDevices.map((d) => d.inventory_id),
      collect_items: collectDevices.map((d) => ({
        inventory_id: d.inventory_id,
        to_location_id: d.to_location_id,
      })),
      transfer_items: transferDevices.map((d) => ({
        inventory_id: d.inventory_id,
        to_location_id: d.to_location_id,
      })),
    };

    setSaving(true);
    setError("");
    setBlockedRemovals([]);

    fetch(`/api/deployments/${deploymentId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          // Lock conflict: someone else is editing this deployment right now.
          if (r.status === 409) {
            throw new Error(
              data.error ||
                "This deployment is currently being edited elsewhere. Please refresh and try again."
            );
          }
          throw new Error(data.error || "Failed to update deployment.");
        }
        return data;
      })
      .then((data) => {
        if (Array.isArray(data.blocked_removals) && data.blocked_removals.length) {
          setBlockedRemovals(data.blocked_removals);
          alert(
            `${data.blocked_removals.length} device(s) could not be removed because ` +
              `they've already been scanned/collected/received. They remain in the deployment.`
          );
        }
        if (onSaved) onSaved();
      })
      .catch((e) => {
        console.error("Update deployment error:", e);
        setError(e.message);
      })
      .finally(() => setSaving(false));
  };

  // ---------- Render helpers ----------

  const renderInventoryTable = ({
    list,
    searchValue,
    onSearchChange,
    actionType,
    showType = true,
  }) => (
    <>
      <div className="deployment-table-search">
        <label>Search Inventory:</label>
        <input
          className="deployment-search-input"
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by model / IMEI / Serial / Location"
        />
      </div>

      <div className="deployment-selection-table-wrapper">
        <table className="deployment-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Model</th>
              <th>Colour</th>
              <th>IMEI</th>
              <th>Serial</th>
              {showType && <th>Type</th>}
              <th>Location</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={showType ? 7 : 6} style={{ textAlign: "center" }}>
                  No available inventory.
                </td>
              </tr>
            ) : (
              list.map((item) => (
                <tr key={item.inventory_id}>
                  <td>
                    <button
                      type="button"
                      className="small-btn"
                      onClick={() => addFromInventory(item, actionType)}
                    >
                      Add
                    </button>
                  </td>
                  <td>{item.asset1_name || item.asset1_id}</td>
                  <td>{item.asset2_name || "-"}</td>
                  <td>{item.imei_number || "-"}</td>
                  <td>{item.serial_number}</td>
                  {showType && <td>{item.inventory_type}</td>}
                  <td>{item.location_name || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  // showDestinationPicker: when true, "To Location" is an editable select
  // (used for COLLECT/TRANSFER). When false, it's read-only text (DEPLOY).
  const renderSelectedTable = ({ title, list, actionType, showDestinationPicker = false }) => (
    <div className="deployment-selected-panel">
      <div className="deployment-selected-header">
        <strong>{title}</strong>
        <span>{list.length} selected</span>
      </div>
      {list.length === 0 ? (
        <p className="deployment-selected-empty">No devices selected.</p>
      ) : (
        <div className="deployment-selected-table-wrapper">
          <table className="deployment-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Model</th>
                <th>Colour</th>
                <th>IMEI</th>
                <th>Serial</th>
                <th>Type</th>
                <th>From Location</th>
                <th>To Location</th>
                <th>Remove</th>
              </tr>
            </thead>
            <tbody>
              {list.map((it, idx) => {
                const wasBlocked = blockedRemovals.some(
                  (b) => b.inventory_id === it.inventory_id && b.action_type === actionType
                );
                return (
                  <tr key={`${it.inventory_id}-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{it.asset1_name || "-"}</td>
                    <td>{it.asset2_name || "-"}</td>
                    <td>{it.imei_number || "-"}</td>
                    <td>{it.serial_number}</td>
                    <td>{it.inventory_type || "-"}</td>
                    <td>{it.from_location_name || "-"}</td>
                    <td>
                      {showDestinationPicker ? (
                        <select
                          value={it.to_location_id || ""}
                          onChange={(e) =>
                            updateItemLocation(actionType, it.inventory_id, e.target.value)
                          }
                        >
                          <option value="">Select destination</option>
                          {locations.map((loc) => (
                            <option key={loc.location_id} value={loc.location_id}>
                              {loc.location_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        it.to_location_name || "-"
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="small-btn danger"
                        onClick={() => removeFrom(actionType, it.inventory_id)}
                        title={
                          wasBlocked
                            ? "Could not be removed on last save — already scanned/collected/received"
                            : undefined
                        }
                      >
                        ✕
                      </button>
                      {wasBlocked && (
                        <div className="deployment-blocked-note">
                          Can't remove — already processed
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ---------- UI ----------

  return (
    <div className="deployment-modal-overlay">
      <div className={`deployment-modal ${
            step >= 2 ? "deployment-modal-fixed" : ""
          }`}
        >
        <button className="deployment-close-btn" onClick={onClose}>
          ✖
        </button>

        {/* HEADER with wizard steps (click to jump) */}
        <div className="deployment-header">
          <h3 className="deployment-header-title">
            Edit Deployment
          </h3>
          <div className="deployment-wizard-steps">
            <span
              className={`deployment-wizard-step ${
                step === 1 ? "active" : ""
              }`}
              onClick={() => setStep(1)}
            >
              INFO
            </span>
            <span
              className={`deployment-wizard-step ${
                step === 2 ? "active" : ""
              }`}
              onClick={() => setStep(2)}
            >
              DEPLOY
            </span>
            <span
              className={`deployment-wizard-step ${
                step === 3 ? "active" : ""
              }`}
              onClick={() => setStep(3)}
            >
              COLLECT
            </span>
            <span
              className={`deployment-wizard-step ${
                step === 4 ? "active" : ""
              }`}
              onClick={() => setStep(4)}
            >
              TRANSFER
            </span>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div className="deployment-body">
            <div className="deployment-scroll-region">
              {loading && <p>Loading deployment…</p>}
              {error && (
                <p className="deployment-error-text">
                  {error}
                </p>
              )}

              {!loading && deployment && (
                <>
                  {/* STEP 1: INFO */}
                  {step === 1 && (
                      <>
                        <div className="deployment-form-group">
                          <label>Deployment Type:</label>
                            <select
                              name="deploymenttype_id"
                              value={form.deploymenttype_id}
                              onChange={handleHeaderChange}
                            >
                              <option value="">Select type</option>
                              {deploymentTypes.map((dt) => (
                                <option
                                  key={dt.deploymenttype_id}
                                  value={dt.deploymenttype_id}
                                >
                                  {dt.deploymenttype_name}
                                </option>
                              ))}
                            </select>
                        </div>

                        <div className="deployment-form-group">
                          <label>Deployment Location:</label>
                          <select
                            name="deployment_location_id"
                            value={form.deployment_location_id}
                            onChange={handleHeaderChange}
                            required
                          >
                            <option value="">Select location</option>
                            {locations.map((loc) => (
                              <option
                                key={loc.location_id}
                                value={loc.location_id}
                              >
                                {loc.location_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="deployment-form-group">
                          <label>Vendor</label>
                            <select
                              name="vendor_id"
                              value={form.vendor_id}
                              onChange={handleHeaderChange}
                            >
                              <option value="">None</option>
                              {vendors.map((v) => (
                                <option
                                  key={v.vendor_id}
                                  value={v.vendor_id}
                                >
                                  {v.vendor_name}
                                </option>
                              ))}
                            </select>
                        </div>
                      </>
                  )}

                  {/* STEP 2: DEPLOY */}
                  {step === 2 && (
                    <>
                      {renderInventoryTable({
                        list: deployAvailable,
                        searchValue: deploySearch,
                        onSearchChange: setDeploySearch,
                        actionType: "DEPLOY",
                        showType: true,
                      })}
                      {renderSelectedTable({
                        title: "Devices to Deploy",
                        list: deployDevices,
                        actionType: "DEPLOY",
                        showDestinationPicker: false,
                      })}
                    </>
                  )}

                  {/* STEP 3: COLLECT */}
                  {step === 3 && (
                    <>
                      <div className="deployment-form-group include-po-row">
                        <div className="deployment-checkbox-row">
                          <input
                            type="checkbox"
                            id="collectIncludePO"
                            checked={collectIncludePO}
                            onChange={(e) =>
                              setCollectIncludePO(e.target.checked)
                            }
                          />
                          <span>
                            Include PO devices in collection inventory
                          </span>
                        </div>
                      </div>

                      <div className="deployment-form-group">
                        <label>Default destination for newly added devices:</label>
                        <select
                          value={defaultCollectToLocationId}
                          onChange={(e) => setDefaultCollectToLocationId(e.target.value)}
                        >
                          <option value="">Select default destination</option>
                          {locations.map((loc) => (
                            <option key={loc.location_id} value={loc.location_id}>
                              {loc.location_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {renderInventoryTable({
                        list: collectAvailable,
                        searchValue: collectSearch,
                        onSearchChange: setCollectSearch,
                        actionType: "COLLECT",
                        showType: true,
                      })}
                      {renderSelectedTable({
                        title: "Devices to Collect",
                        list: collectDevices,
                        actionType: "COLLECT",
                        showDestinationPicker: true,
                      })}
                    </>
                  )}

                  {/* STEP 4: TRANSFER */}
                  {step === 4 && (
                    <>
                      <div className="deployment-form-group">
                        <label>Default destination for newly added devices:</label>
                        <select
                          value={defaultTransferToLocationId}
                          onChange={(e) => setDefaultTransferToLocationId(e.target.value)}
                        >
                          <option value="">Select default destination</option>
                          {locations.map((loc) => (
                            <option key={loc.location_id} value={loc.location_id}>
                              {loc.location_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {renderInventoryTable({
                        list: transferAvailable,
                        searchValue: transferSearch,
                        onSearchChange: setTransferSearch,
                        actionType: "TRANSFER",
                        showType: true,
                      })}
                      {renderSelectedTable({
                        title: "Devices to Transfer",
                        list: transferDevices,
                        actionType: "TRANSFER",
                        showDestinationPicker: true,
                      })}
                    </>
                  )}
                </>
              )}
            </div>

            {/* FOOTER */}
            <div className="deployment-footer">
              <button
                type="button"
                className="deployment-btn-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="deployment-btn-primary"
                disabled={saving || loading}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}